import React from 'react'
import { Icon } from './Icon'
import { Navbar } from './Navbar'
import { Hero } from './Hero'
import { TrustBar, AboutPreview, WhyUs, BusinessSegments } from './HomeSectionsA'
import { Industries, CTASection } from './HomeSectionsB'
import { FeaturePanels } from './FeaturePanels'
import { Footer } from './Footer'
import { ServicesPage, ServiceDetail } from './ServicesPage'
import { AboutPage, ProcessPage, IndustriesPage } from './PagesA'
import { CertificationsPage, CareersPage, ClientPortalPage } from './PagesB'
import { GalleryPage } from './GalleryPage'
import { PartnersPage } from './PartnersPage'
import { ContactPage } from './ContactPage'

function HomePage({ onNavigate }) {
  return (
    <div>
      <Hero onNavigate={onNavigate} />
      <TrustBar />
      <AboutPreview onNavigate={onNavigate} />
      <WhyUs />
      <BusinessSegments onNavigate={onNavigate} />
      <FeaturePanels onNavigate={onNavigate} />
      <Industries />
      <CTASection onNavigate={onNavigate} />
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = React.useState(0);
  React.useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? (h.scrollTop || window.scrollY) / max : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 60, background: 'transparent' }}>
      <div style={{ height: '100%', width: `${p * 100}%`, background: 'var(--grad-energy)', transition: 'width 0.1s linear' }} />
    </div>
  );
}

function FloatingCTA({ onNavigate }) {
  return (
    <button onClick={() => onNavigate('Contact')} aria-label="Get consultation" className="adr-float-cta" style={{
      position: 'fixed', right: 22, bottom: 22, zIndex: 55,
      display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 20px',
      borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer',
      background: 'var(--energy)', color: 'var(--blue-900)', boxShadow: 'var(--glow-green)',
      fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 14, transition: 'transform var(--dur-fast)',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
      <Icon name="message-circle" style={{ width: 20, height: 20 }} />
      <span className="adr-float-cta-label">Talk to us</span>
    </button>
  );
}

const PAGE_TO_HASH = {
  Home: '', About: 'about', Services: 'services',
  Industries: 'industries', Process: 'process', Partners: 'partners',
  Gallery: 'gallery', Certifications: 'certifications', Careers: 'careers',
  Contact: 'contact', Portal: 'portal',
};

function parseHash() {
  const h = (window.location.hash || '').replace(/^#\/?/, '');
  if (!h) return { page: 'Home', service: null };
  if (h.startsWith('service/')) return { page: 'Service', service: h.slice(8) };
  const entry = Object.entries(PAGE_TO_HASH).find(([, v]) => v === h);
  return { page: entry ? entry[0] : 'Home', service: null };
}

export default function App() {
  const [route, setRoute] = React.useState(() => parseHash());

  const navigate = React.useCallback((arg) => {
    let next;
    if (typeof arg === 'string') {
      const map = {
        'Home': 'Home', 'About': 'About', 'About Us': 'About', 'Services': 'Services',
        'Industries': 'Industries', 'Process': 'Process', 'Partners': 'Partners', 'Gallery': 'Gallery',
        'Careers': 'Careers', 'Certifications': 'Certifications', 'Contact': 'Contact',
        'Portal': 'Portal', 'Client Portal': 'Portal',
      };
      next = { page: map[arg] || 'Home', service: null };
    } else if (arg && arg.page === 'Service') {
      next = { page: 'Service', service: arg.service };
    } else {
      next = { page: (arg && arg.page) || 'Home', service: null };
    }
    const hash = next.page === 'Service'
      ? `service/${next.service}`
      : (PAGE_TO_HASH[next.page] || '');
    window.history.pushState(next, '', hash ? `${window.location.pathname}#${hash}` : window.location.pathname);
    setRoute(next);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  React.useEffect(() => {
    const onPop = (e) => {
      setRoute(e.state || parseHash());
      window.scrollTo({ top: 0, behavior: 'auto' });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const { page, service } = route;
  const isPortal = page === 'Portal';

  return (
    <React.Fragment>
      {!isPortal && <ScrollProgress />}
      {!isPortal && <Navbar current={page} onNavigate={navigate} dark={true} />}
      <main key={page + (service || '')} className="adr-page-enter">
        {page === 'Home' && <HomePage onNavigate={navigate} />}
        {page === 'About' && <AboutPage onNavigate={navigate} />}
        {page === 'Services' && <ServicesPage onNavigate={navigate} />}
        {page === 'Service' && <ServiceDetail serviceKey={service} onNavigate={navigate} />}
        {page === 'Industries' && <IndustriesPage onNavigate={navigate} />}
        {page === 'Process' && <ProcessPage onNavigate={navigate} />}
        {page === 'Partners' && <PartnersPage onNavigate={navigate} />}
        {page === 'Gallery' && <GalleryPage onNavigate={navigate} />}
        {page === 'Certifications' && <CertificationsPage onNavigate={navigate} />}
        {page === 'Careers' && <CareersPage onNavigate={navigate} />}
        {page === 'Contact' && <ContactPage onNavigate={navigate} />}
        {page === 'Portal' && <ClientPortalPage onNavigate={navigate} />}
      </main>
      {!isPortal && <Footer onNavigate={navigate} />}
      {!isPortal && page !== 'Contact' && <FloatingCTA onNavigate={navigate} />}
    </React.Fragment>
  );
}
